import React, { useState } from 'react';
import { SHeaderSortable, SPagination } from '@avalara/skylab-sdk/react';
import _ from 'lodash';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import { Link } from 'react-router-dom';
import FiltersPanelWrapper from './FiltersPanelWrapper';
import ConfirmationModal from '../modals/ConfirmationModal';
import Logger, { LOG_LEVELS } from '../../utils/logger';
import { userIdentityLinkTo } from '../../utils/userIdentityRoutes';

const FORM_NAME = 'USERS_TABLE';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

const ALL_COMPANIES_GRANT = '*';
const MAX_COMPANIES_COUNT = Number.MAX_VALUE;

const UsersTable = (props) => {

    const [sortingConfig, setSortingConfig] = useState({ columnId: undefined, order: undefined });
    const [pagingConfig, setPagingConfig] = useState({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
    const [filteringConfig, setFilteringConfig] = useState({ companies: undefined, roles: undefined });
    const [deleteUserDetails, setDeleteUserDetails] = useState({ openConfirmationDialog: false, userId: undefined });
    const [renderedUsersCount, setRenderedUsersCount] = useState(props.users?.length);

    const getCompnaiesCount = (user) => {
        if (!user.companyGrants) {
            return 0;
        }
        if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
            return MAX_COMPANIES_COUNT;
        }
        return user.companyGrants.length;
    }

    const getDisplayCompanyValue = (user) => {
        if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
            return LANGUAGES_CONTROLLER.format(props.languages, "UserManagementAllCompaniesText");
        }
        if (user.companiesCount === 1 && props.companiesLookup) {
            return props.companiesLookup[user.companyGrants[0]];
        }
        return LANGUAGES_CONTROLLER.format(props.languages, "UserManagementCompaniesText", [user.companiesCount]);
    }

    props?.users?.map(user => {
        user.companiesCount = getCompnaiesCount(user);
        user.displayCompanyValue = getDisplayCompanyValue(user);
    });

    const onSort = (e) => {
        const columnId = e.detail.id;
        const order = (e.detail.ascending) ? "asc" : "desc";
        if (["id", "firstName", "displayCompanyValue", "role"].includes(columnId)) {
            setSortingConfig({ columnId: columnId, order: order });
        }
    }

    const onPaginate = (e) => {
        const startIndex = e.detail.startIndex;
        const endIndex = e.detail.endIndex;
        const rowsPerPage = e.detail.rowsPerPage;
        setPagingConfig({ startIndex: startIndex, endIndex: endIndex, rowsPerPage: rowsPerPage });
    }

    const onDeleteUserButtonClick = (userId) => {
        log.clearAllDictionaryErrors();
        setDeleteUserDetails({ openConfirmationDialog: true, userId: userId })
    }

    const deleteUserConfirmationModalYesHandler = () => {
        props.deleteUserHandler(deleteUserDetails.userId)
        closeDeleteUserModal();
    }

    const deleteUserConfirmationModalCancelHandler = () => {
        closeDeleteUserModal();
    }

    const closeDeleteUserModal = () => {
        setDeleteUserDetails({ openConfirmationDialog: false, userId: undefined })
    }

    const getFilteredUsers = () => {
        const filterByCompany = (users) => {
            if (!filteringConfig.companies?.length) {
                return users;
            }
            return users.filter((user) => {
                return user.companyGrants.includes(ALL_COMPANIES_GRANT) || _.intersection(user.companyGrants, filteringConfig.companies).length > 0;
            });
        }
        const filterByRole = (users) => {
            if (!filteringConfig.roles?.length) {
                return users;
            }
            return users.filter((user) => filteringConfig.roles.includes(user.role));
        }
        if (!props?.users) {
            return [];
        }
        const byCompany = filterByCompany(props.users);
        return filterByRole(byCompany);
    }

    const generateSortedPage = (users) => {

        if (users?.length == 0) {
            return null;
        }

        let sortedUsers = users;

        if (users.length != renderedUsersCount) {
            setPagingConfig({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
            setRenderedUsersCount(users.length);
        }

        if (sortingConfig.columnId) {
            if (sortingConfig.columnId === "displayCompanyValue") {
                sortedUsers = _.orderBy(users, ["companiesCount", sortingConfig.columnId], [sortingConfig.order, sortingConfig.order])
            } else {
                sortedUsers = _.orderBy(users, [sortingConfig.columnId], [sortingConfig.order])
            }
        }
        const currentPage = sortedUsers.slice(pagingConfig.startIndex, pagingConfig.endIndex + 1);
        return currentPage.map(user => {
            const linkToUrl = userIdentityLinkTo('/ViewUserDetails', user.id);
            return (
                <tr key={user.id}>
                    <td className="forty-width">
                        <Link className="tertiary bold break-word" to={linkToUrl}>
                            {user.firstName} {user.lastName}
                        </Link>
                    </td>
                    <td className="twenty-width">{user.role}</td>
                    <td className="twenty-width">
                        {user.displayCompanyValue}
                    </td>
                    <td className="twenty-width">{user.id}</td>
                    <td>
                        <button data-testid="delete-user-button" class="ghost inline-table-button" onClick={() => onDeleteUserButtonClick(user.id)}>
                            <s-icon name="trash" class="blue-dark" role="img" aria-label="delete"></s-icon>
                        </button>
                    </td>
                </tr>
            );
        });
    }

    const filteredUsers = getFilteredUsers();
    const usersRows = generateSortedPage(filteredUsers);

    return (
        <div>
            <FiltersPanelWrapper
                open={props.toggleFilterPanel}
                onDismiss={() => props.setToggleFilterPanel(false)}
                allCompanyOptions={props.allCompanyOptions}
                setFilteringConfig={setFilteringConfig}
            />
            <s-table-container data-testid="users-table" tabindex="0">
                <table>
                    <thead>
                        <tr>
                            <th data-testid="first-name-column" className="pb0 pt4">
                                <SHeaderSortable id="firstName"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "UserManagementName")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="role-column" className="pb0 pt4">
                                <SHeaderSortable id="role"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "UserRole")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="company-column" className="pb0 pt4">
                                <SHeaderSortable id="displayCompanyValue"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "company")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="email-column" className="pb0 pt4">
                                <SHeaderSortable id="id"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "UserEmailAddressHeading")}
                                    onS-sort={onSort} />
                            </th>
                            <th data-testid="delete-user-column" className="pb0 pt4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersRows}
                    </tbody>
                </table>
            </s-table-container>
            <SPagination className="mt20 mb170"
                totalRecords={renderedUsersCount}
                startIndex={pagingConfig.startIndex}
                rowsPerPage={pagingConfig.rowsPerPage}
                onS-paginate={onPaginate} />

            <ConfirmationModal
                isModalPopupOpen={deleteUserDetails.openConfirmationDialog}
                modelTitle="UserManagementDeleteUser"
                modelMessage="UserManagementDeleteUserConfirmation"
                onYesClickHandler={deleteUserConfirmationModalYesHandler}
                onCancelClickHandler={deleteUserConfirmationModalCancelHandler}
            />
        </div>
    );
};

export default UsersTable;
